import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { api } from './api'
import { pickReferrer, ReferrerSource } from './referral'
import { readRef } from './refstore'

export interface ReferralInfo {
  address: string
  /** the /r/<id> of this wallet: handle while public, else the wallet */
  id: string
  invited_by: { address: string; handle: string | null } | null
}

/** The wallet's referral id and its bound inviter (social API referrals/<wallet>, public). */
export function useReferralInfo(wallet?: string | null) {
  const w = wallet && /^0x[0-9a-fA-F]{40}$/.test(wallet) ? wallet.toLowerCase() : null
  const { data, error, isLoading } = useSWR<ReferralInfo>(w ? `referral-info:${w}` : null, () => api<ReferralInfo>(`referrals/${w}`), {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    shouldRetryOnError: false,
  })
  return { info: data, error, settled: !w || Boolean(data) || Boolean(error), loading: isLoading }
}

/**
 * The referrer to prefill for this wallet (V4 competition creation, NFT purchase): bound inviter > old ?ref= link > stored
 * invite (refstore.ts), never the wallet or the recipient itself. `ready` turns true once the bound value has been read
 * (or failed), so a form applies the prefill once and the user's own edits are never overwritten.
 */
export function useDefaultReferrer({ account, recipient, query }: { account?: string | null; recipient?: string | null; query?: unknown }) {
  const { info, settled } = useReferralInfo(account)
  const [stored, setStored] = useState<string | undefined>(undefined)
  useEffect(() => setStored(readRef()), [account])
  const pick = useMemo(
    () => pickReferrer({ bound: info?.invited_by?.address ?? null, query, stored, self: account, recipient }),
    [info?.invited_by?.address, query, stored, account, recipient],
  )
  const invitedBy = info?.invited_by ?? null
  return { referrer: pick?.address, source: pick?.source as ReferrerSource | undefined, invitedBy, ready: Boolean(account) && settled }
}

/**
 * Who invited this wallet: `bound` once the first CADINU sign-in bound it (permanent), else `pending` = the first-touch
 * invite stored in this browser, which the first sign-in will bind. Never the wallet itself.
 */
export function useInviter(account?: string | null) {
  const { info, settled } = useReferralInfo(account)
  const [stored, setStored] = useState<string | undefined>(undefined)
  useEffect(() => setStored(readRef()), [account])
  const bound = info?.invited_by ?? null
  const pendingAddr = !bound && settled && stored && stored.toLowerCase() !== (account ?? '').toLowerCase() ? stored : undefined
  const { info: pendingInfo } = useReferralInfo(pendingAddr)
  const pending = pendingAddr ? { address: pendingAddr, handle: pendingInfo && !pendingInfo.id.startsWith('0x') ? pendingInfo.id : null } : null
  return { bound, pending, settled }
}
